import { Autocomplete, Box, TextField, Popper } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import useTheme from "../../../../common/hooks/useTheme";

export const CustomPopper = (props: any) => {
  const theme = useTheme();
  const { anchorEl, style, ...otherProps } = props;

  // Check if popper would be outside the container
  const isOutsideContainer = () => {
    if (!anchorEl) return false;

    const scrollableContainer =
      anchorEl.closest("[data-simplebar]") ||
      anchorEl.closest('[style*="overflow"]') ||
      anchorEl.closest('.MuiBox-root[style*="overflow"]');

    if (!scrollableContainer) return false;

    const containerRect = scrollableContainer.getBoundingClientRect();
    const anchorRect = anchorEl.getBoundingClientRect();

    // Check if anchor is outside the visible area of the container
    const isAnchorVisible =
      anchorRect.bottom > containerRect.top &&
      anchorRect.top < containerRect.bottom &&
      anchorRect.right > containerRect.left &&
      anchorRect.left < containerRect.right;

    return !isAnchorVisible;
  };

  if (isOutsideContainer()) {
    return null; // Don't render the popper if it would be outside
  }

  return (
    <Popper
      {...otherProps}
      anchorEl={anchorEl}
      style={{
        width: anchorEl ? anchorEl.clientWidth : undefined,
        boxShadow: "var(--box-shadow-general)",
        borderRadius: "8px",
        border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
        overflowX: "hidden",
        zIndex: 1300, // Higher z-index to appear above modal content
        ...style,
      }}
      placement="bottom-start"
      modifiers={[
        {
          name: "flip",
          enabled: true,
          options: {
            fallbackPlacements: ["top-start", "bottom-start"],
          },
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            boundary: "clippingParents",
            padding: 8,
          },
        },
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
      ]}
    />
  );
};

const CustomArrowIcon = ({ focused }: { focused: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M7.99943 11.0006C7.73424 11.0005 7.47992 10.8951 7.29243 10.7076L3.29243 6.70757C3.11027 6.51897 3.00948 6.26636 3.01176 6.00417C3.01403 5.74197 3.1192 5.49116 3.30461 5.30575C3.49002 5.12034 3.74083 5.01517 4.00303 5.0129C4.26523 5.01062 4.51783 5.11141 4.70643 5.29357L7.99943 8.58657L11.2924 5.29357C11.3847 5.19806 11.495 5.12188 11.617 5.06947C11.739 5.01706 11.8703 4.98947 12.003 4.98832C12.1358 4.98717 12.2675 5.01247 12.3904 5.06275C12.5133 5.11303 12.6249 5.18728 12.7188 5.28117C12.8127 5.37507 12.887 5.48672 12.9373 5.60962C12.9875 5.73251 13.0128 5.86419 13.0117 5.99697C13.0105 6.12975 12.9829 6.26097 12.9305 6.38297C12.8781 6.50498 12.8019 6.61532 12.7064 6.70757L8.70643 10.7076C8.51894 10.8951 8.26463 11.0005 7.99943 11.0006Z"
      fill={focused ? "#1064DB" : "#3B3F42"}
    />
  </svg>
);

const CustomAutoComplete = (props: {
  label?: string;
  placeholder?: string;
  defaultValue?: string | null;
  options: any[];
  value?: string | null;
  onChange?: (value: string | null) => void;
  sx?: any;
}) => {
  const [internalValue, setInternalValue] = useState<string | null>(
    props.value || props.defaultValue || null
  );
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      if (isOpen && autocompleteRef.current) {
        const scrollableContainer =
          autocompleteRef.current.closest("[data-simplebar]") ||
          autocompleteRef.current.closest('[style*="overflow"]') ||
          autocompleteRef.current.closest('.MuiBox-root[style*="overflow"]');

        if (scrollableContainer) {
          const containerRect = scrollableContainer.getBoundingClientRect();
          const inputRect = autocompleteRef.current.getBoundingClientRect();

          const isInputVisible =
            inputRect.bottom > containerRect.top &&
            inputRect.top < containerRect.bottom &&
            inputRect.right > containerRect.left &&
            inputRect.left < containerRect.right;

          if (!isInputVisible) {
            // Find and blur the input to close the popper naturally
            const input = autocompleteRef.current.querySelector("input");
            if (input) {
              input.blur();
            }
            setIsOpen(false);
          }
        }
      }
    };

    if (isOpen) {
      const scrollableContainer =
        autocompleteRef.current?.closest("[data-simplebar]") ||
        autocompleteRef.current?.closest('[style*="overflow"]') ||
        autocompleteRef.current?.closest('.MuiBox-root[style*="overflow"]') ||
        document;

      scrollableContainer.addEventListener("scroll", handleScroll, true);
      return () => {
        scrollableContainer.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [isOpen]);

  const value = props.value !== undefined ? props.value : internalValue;
  const isDisabled = props.options.length === 0;

  return (
    <Box ref={autocompleteRef} sx={{ flex: 1, position: "relative" }}>
      <Autocomplete
        disabled={isDisabled}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        value={value}
        onChange={(event: any, newValue: string | null) => {
          if (props.onChange) {
            props.onChange(newValue);
          } else {
            setInternalValue(newValue);
          }
          setIsOpen(false);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        id="controllable-states-demo"
        options={props.options}
        getOptionLabel={(option) => option}
        renderOption={(props, option, state) => (
          <li {...props} key={`${option}-${state.index}`}>
            {option}
          </li>
        )}
        popupIcon={<CustomArrowIcon focused={isFocused && !isDisabled} />}
        PopperComponent={CustomPopper}
        sx={{
          width: "100%",
          ...props.sx,
          "& .MuiAutocomplete-popupIndicator": {
            padding: "4px",
            "&:hover": {
              borderRadius: "50%",
            },
          },
          "& .MuiAutocomplete-clearIndicator": {
            color: isFocused
              ? theme.colors_ds.PRIMARY_ICON_MAIN
              : theme.colors_ds.NEUTRAL_ICON_WEAK,
          },
          "& .MuiInputLabel-root": {
            fontFamily: "var(--font-Medium)",
            fontSize: "10px",
            backgroundColor: "transparent",
            transition: "transform 0.2s ease-in-out",
            color: isDisabled
              ? theme.colors_ds.NEUTRAL_TEXT_WEAK
              : theme.colors_ds.NEUTRAL_TEXT_WEAK,
            transform: Boolean(value || inputValue || isFocused)
              ? "translate(12px, 8px) scale(1)"
              : "translate(12px, 16px) scale(1.5)",
            "&.Mui-focused": {
              color: isDisabled
                ? theme.colors_ds.NEUTRAL_TEXT_WEAK
                : theme.colors_ds.PRIMARY_ICON_MAIN,
            },
            "&.Mui-disabled": {
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
            },
          },
          "& .MuiOutlinedInput-root": {
            height: "56px",
            borderRadius: "8px",
            backgroundColor: isDisabled
              ? theme.colorTokens.N[30]
              : theme.colors_ds.NEUTRAL_BG_MAIN,
            "& input": {
              caretColor: theme.colors_ds.PRIMARY_ICON_MAIN,
              color: isDisabled
                ? theme.colors_ds.NEUTRAL_TEXT_WEAK
                : theme.colors_ds.NEUTRAL_TEXT_STRONG,
            },
            "& fieldset": {
              borderColor: isDisabled
                ? theme.colors_ds.NEUTRAL_BORDER_WEAK
                : theme.colors_ds.NEUTRAL_BORDER_WEAK,
              top: 0,
            },
            "& legend": {
              display: "none",
            },
            "&:hover fieldset": {
              borderColor: isDisabled
                ? theme.colors_ds.NEUTRAL_BORDER_WEAK
                : theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
            },
            "&.Mui-focused fieldset": {
              borderColor: isDisabled
                ? theme.colors_ds.NEUTRAL_BORDER_WEAK
                : theme.colors_ds.PRIMARY_ICON_MAIN,
              borderWidth: "1px",
            },
            "&.Mui-disabled": {
              backgroundColor: theme.colors_ds.NEUTRAL_BG_WEAK,
              "& fieldset": {
                borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
              },
            },
          },
          "& .MuiOutlinedInput-input": {
            marginTop: props.label ? "12px" : "0",
          },
        }}
        slotProps={{
          listbox: {
            sx: {
              maxHeight: "200px",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
              },
              "& .MuiAutocomplete-option": {
                fontFamily: "var(--font-Medium)",
                color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                fontSize: "14px",
                padding: "8px 16px",
                "&:hover": {
                  backgroundColor: theme.colorTokens.N[40],
                },
              },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label ?? null}
            placeholder={props.placeholder ?? "Select an option"}
            onFocus={() => !isDisabled && setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& input": {
                  color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
                  fontFamily: "var(--font-Medium)",
                  fontSize: "12px",
                  lineHeight: "24px",
                },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default CustomAutoComplete;
